import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import OutputPanel from './OutputPanel';
import UnifiedFileUpload from './UnifiedFileUpload';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/ui/tabs.tsx';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/ui/dropdown-menu.tsx';
import { curveAiChat } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OUTPUT_TABS = [
  { key: 'mockPaper', label: 'Mock Papers' },
  { key: 'studyGuide', label: 'Study Guide' },
  { key: 'importantTopics', label: 'Important Topics' },
  { key: 'examExpectations', label: 'Exam Expectations' },
];

const Dashboard = () => {
  const {
    currentDocument,
    setCurrentDocument,
    activeTab,
    setActiveTab,
    generationResults,
    setGenerationResults,
  } = useAppContext();

  const { currentUser } = useAuth();

  // Curve Ai chat state
  const [curveAiMessages, setCurveAiMessages] = useState([
    { role: 'ai', content: 'Hi! I am Curve Ai. Ask me anything about your uploaded docs.' }
  ]);
  const [curveAiInput, setCurveAiInput] = useState('');
  const [curveAiLoading, setCurveAiLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatHistoryRef = useRef(null);

  // State for About modal
  const [showAbout, setShowAbout] = useState(false);

  // Handler to set the combined uploaded set as the current document
  const handleUploadSuccess = (res) => {
    if (res && res.documentId) {
      setCurrentDocument({
        documentId: res.documentId,
        filenames: res.filenames,
        // Optionally, add more info about which files came from which field
      });
    }
  };

  // Helper to get the label for the current active tab
  const getActiveTabLabel = () => {
    const found = OUTPUT_TABS.find(tab => tab.key === activeTab);
    return found ? found.label : 'Output Options';
  };

  // Handle sending a message to Curve Ai
  const handleCurveAiSend = async (e) => {
    e.preventDefault();
    if (!curveAiInput.trim()) return;
    const userMessage = { role: 'user', content: curveAiInput };
    setCurveAiMessages(msgs => [...msgs, userMessage]);
    setCurveAiInput('');
    setCurveAiLoading(true);
    try {
      // Prepare chat history for context (exclude initial AI greeting)
      const history = curveAiMessages.slice(1).map(m => ({ role: m.role, content: m.content }));
      const res = await curveAiChat(curveAiInput, currentDocument?.documentId, history);
      setCurveAiMessages(msgs => [...msgs, { role: 'ai', content: res.reply }]);
    } catch (err) {
      setCurveAiMessages(msgs => [...msgs, { role: 'ai', content: 'Sorry, Curve Ai could not answer due to an error.' }]);
    }
    setCurveAiLoading(false);
  };

  // Dynamic Quick Actions and Suggestions
  function getDynamicActionsAndSuggestions(messages) {
    const lastMsg = messages[messages.length - 1];
    let quickActions = [
      { label: 'Quiz me', message: 'Curve, quiz me on the most important points.' },
      { label: 'Summarize', message: 'Curve, summarize this document.' },
      { label: 'Explain', message: 'Curve, explain this topic in simple terms.' },
      { label: 'Make a study plan', message: 'Curve, help me create a study plan based on this document.' },
    ];
    let suggestions = [
      'Can you explain that in simpler terms?',
      'What should I focus on next?',
    ];

    if (lastMsg) {
      const content = lastMsg.content.toLowerCase();
      if (content.includes('summary') || content.includes('summarize')) {
        quickActions = [
          { label: 'Quiz me', message: 'Curve, quiz me on the summary.' },
          { label: 'Explain key points', message: 'Curve, explain the key points from the summary.' },
          { label: 'Make a study plan', message: 'Curve, help me make a study plan based on the summary.' },
        ];
        suggestions = [
          'Can you quiz me on this summary?',
          'What are the most important points to remember?'
        ];
      } else if (content.includes('quiz')) {
        quickActions = [
          { label: 'Show answers', message: 'Curve, show me the answers to the quiz.' },
          { label: 'Explain answers', message: 'Curve, explain the answers to the quiz.' },
          { label: 'Make a study plan', message: 'Curve, help me make a study plan based on my quiz performance.' },
        ];
        suggestions = [
          'Can you explain why these are the answers?',
          'What topics should I review based on this quiz?'
        ];
      } else if (content.includes('plan')) {
        quickActions = [
          { label: 'Quiz me', message: 'Curve, quiz me on my study plan.' },
          { label: 'Summarize', message: 'Curve, summarize my study plan.' },
          { label: 'Explain', message: 'Curve, explain how to follow this study plan.' },
        ];
        suggestions = [
          'How can I stick to this plan?',
          'What should I do if I fall behind?'
        ];
      } else if (content.includes('explain')) {
        quickActions = [
          { label: 'Quiz me', message: 'Curve, quiz me on what you just explained.' },
          { label: 'Summarize', message: 'Curve, summarize what you just explained.' },
          { label: 'Make a study plan', message: 'Curve, help me make a study plan for this topic.' },
        ];
        suggestions = [
          'Can you give me a mnemonic for this?',
          'What should I focus on next?'
        ];
      }
    }
    return { quickActions, suggestions };
  }

  const handleQuickAction = async (msg) => {
    setCurveAiInput('');
    setCurveAiMessages(msgs => [...msgs, { role: 'user', content: msg }]);
    setCurveAiLoading(true);
    try {
      const history = [...curveAiMessages.slice(1), { role: 'user', content: msg }].map(m => ({ role: m.role, content: m.content }));
      const res = await curveAiChat(msg, currentDocument?.documentId, history);
      setCurveAiMessages(msgs => [...msgs, { role: 'ai', content: res.reply }]);
    } catch (err) {
      setCurveAiMessages(msgs => [...msgs, { role: 'ai', content: 'Sorry, Curve Ai could not answer due to an error.' }]);
    }
    setCurveAiLoading(false);
  };

  // Dynamic suggestions
  const lastAiIndex = [...curveAiMessages].reverse().findIndex(m => m.role === 'ai');
  const showSuggestions = lastAiIndex === 0 && curveAiMessages.length > 1 && !curveAiLoading && currentDocument?.documentId;
  const { quickActions, suggestions } = getDynamicActionsAndSuggestions(curveAiMessages);

  // Handler to clear chat
  const handleClearChat = () => {
    setCurveAiMessages([
      { role: 'ai', content: 'Hi! I am Curve Ai. Ask me anything about your uploaded docs.' }
    ]);
  };

  // Handler to download chat as .txt
  const handleDownloadChat = () => {
    const chatText = curveAiMessages.map(m => `${m.role === 'ai' ? 'Curve Ai' : 'You'}: ${m.content}`).join('\n\n');
    const blob = new Blob([chatText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'curve-ai-chat.txt';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 0);
  };

  // Auto-scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [curveAiMessages, curveAiLoading]);

  return (
    <div className="min-h-screen bg-[#f6f7fb] flex flex-col pt-10">
      {/* Header */}
      <header className="w-full px-4 sm:px-6 py-4 flex items-center border-b bg-white shadow-sm">
        <span className="text-xl sm:text-2xl font-bold tracking-tight bg-gradient-to-r from-orange-400 to-yellow-300 bg-clip-text text-transparent">
          Welcome, {currentUser?.displayName || currentUser?.email?.split('@')[0] || 'there'}
        </span>
      </header>
      {/* Main layout */}
      <main className="flex-1 flex flex-col lg:flex-row gap-4 px-2 sm:px-4 py-4 pb-24 min-h-0 grow">
        {/* Sources (Left) */}
        <section className="w-full lg:w-[22%] min-w-0 lg:min-w-[240px] lg:max-w-[320px] flex-shrink-0 flex flex-col gap-3 order-1 mb-4 lg:mb-0">
          <UnifiedFileUpload onUploadSuccess={handleUploadSuccess} />
        </section>
        {/* Output (Center) */}
        <section className="w-full lg:flex-1 flex flex-col gap-3 order-3 lg:order-2 mb-4 lg:mb-0">
          <Card className="flex-1 flex flex-col min-h-[320px]">
            <CardHeader className="pb-2">
              <div className="w-full flex justify-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-full px-4 py-2 bg-gray-100 rounded-lg text-base font-medium flex items-center justify-between">
                      {getActiveTabLabel()}
                      <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-56">
                    {OUTPUT_TABS.map(tab => (
                      <DropdownMenuItem
                        key={tab.key}
                        onSelect={() => setActiveTab(tab.key)}
                        className={activeTab === tab.key ? 'bg-accent text-accent-foreground' : ''}
                      >
                        {tab.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto min-h-[200px] max-h-[60vh]">
              <OutputPanel
                currentDocument={currentDocument}
                activeTab={activeTab}
                generationResults={generationResults}
                setGenerationResults={setGenerationResults}
              />
            </CardContent>
          </Card>
        </section>
        {/* Studio/Curve Ai Chat (Right) */}
        <section className="w-full lg:w-[28%] min-w-0 lg:min-w-[320px] lg:max-w-[420px] flex flex-col order-2 lg:order-3">
          <Card className="flex-1 flex flex-col bg-white rounded-2xl shadow-lg overflow-hidden min-h-[320px]">
            {/* Chat header */}
            <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'linear-gradient(90deg, #3a8dde 0%, #6ec6ff 100%)', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}>
              <span className="w-10 h-10 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center text-blue-600 font-bold text-lg">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="12" fill="#e3f2fd"/><text x="50%" y="55%" textAnchor="middle" fill="#1976d2" fontSize="14" fontWeight="bold" dy=".3em">C</text></svg>
              </span>
              <div className="flex flex-col">
                <span className="text-white font-semibold text-base">Chat with Curve Ai</span>
                <span className="text-xs text-blue-100">We are online!</span>
              </div>
              <div className="ml-auto text-white opacity-70 cursor-pointer relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="focus:outline-none p-1" aria-label="Chat options">
                      &#8942;
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onSelect={handleClearChat} className="cursor-pointer">Clear chat</DropdownMenuItem>
                    <DropdownMenuItem onSelect={handleDownloadChat} className="cursor-pointer">Download chat</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setShowAbout(true)} className="cursor-pointer">About Curve Ai</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {/* Chat history */}
            <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2" ref={chatHistoryRef} style={{ minHeight: '120px', maxHeight: '45vh' }}>
              {!currentDocument?.documentId ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 animate-fade-in m-0" style={{ fontSize: '1rem', gap: '0.5rem' }}>
                  <span className="text-2xl mb-1">📄</span>
                  <span>Please upload a document to chat with Curve Ai.</span>
                </div>
              ) : curveAiMessages.length === 1 && !curveAiLoading ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 animate-fade-in m-0" style={{ fontSize: '1rem', gap: '0.5rem' }}>
                  <span className="text-2xl mb-1">💬</span>
                  <span>Start chatting with Curve Ai about your uploaded docs!</span>
                </div>
              ) : null}
              {curveAiMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'ai' ? (
                    <div className="flex items-end gap-2">
                      <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-base">C</span>
                      <div className="flex flex-col items-start max-w-[70vw]">
                        <span className="text-xs font-semibold mb-1 pl-1">Curve Ai</span>
                        <span className="bg-gray-100 text-gray-800 px-3 py-1.5 rounded-2xl rounded-bl-sm shadow break-words text-sm font-normal leading-snug" style={{ borderRadius: '1.5rem 1.5rem 1.5rem 0.5rem' }}>
                          {msg.content}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-end gap-2">
                      <div className="flex flex-col items-end max-w-[70vw]">
                        <span className="text-xs font-semibold mb-1 pr-1">You</span>
                        <span className="bg-gradient-to-r from-blue-500 to-blue-400 text-white px-3 py-1.5 rounded-2xl rounded-br-sm shadow break-words text-sm font-normal leading-snug" style={{ borderRadius: '1.5rem 1.5rem 0.5rem 1.5rem' }}>
                          {msg.content}
                        </span>
                      </div>
                      <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-base">U</span>
                    </div>
                  )}
                </div>
              ))}
              {curveAiLoading && (
                <div className="flex items-end gap-2 animate-fade-in">
                  <span className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-base">C</span>
                  <span className="bg-gray-100 text-gray-500 px-4 py-2 rounded-2xl rounded-bl-sm shadow max-w-[70vw] flex items-center gap-2 text-sm" style={{ borderRadius: '1.5rem 1.5rem 1.5rem 0.5rem' }}>
                    <svg className="animate-spin h-4 w-4 mr-1 text-blue-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                    Curve Ai is typing...
                  </span>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            {/* Chat input */}
            <form className="relative flex items-center gap-2 px-3 pb-3 pt-2 bg-white" onSubmit={handleCurveAiSend} style={{ borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem' }}>
              <input
                type="text"
                className="flex-1 border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="Enter your message..."
                value={curveAiInput}
                onChange={e => setCurveAiInput(e.target.value)}
                disabled={curveAiLoading || !currentDocument?.documentId}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleCurveAiSend(e);
                  }
                }}
              />
              <button
                type="submit"
                className="w-11 h-11 ml-1 bg-gradient-to-br from-blue-500 to-blue-400 text-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 hover:shadow-xl transition-transform disabled:opacity-60 focus:outline-none z-20"
                disabled={curveAiLoading || !curveAiInput.trim() || !currentDocument?.documentId}
                style={{ boxShadow: '0 2px 8px rgba(80,150,255,0.15)' }}
              >
                {curveAiLoading ? (
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg>
                ) : (
                  <span className="text-xl font-bold leading-none flex items-center justify-center w-full h-full" style={{lineHeight: 'normal', marginTop: '-2px'}}>→</span>
                )}
              </button>
            </form>
            {/* Quick Actions */}
            {currentDocument?.documentId && (
              <div className="flex flex-wrap gap-2 px-3 pb-2">
                {quickActions.map(action => (
                  <button
                    key={action.label}
                    type="button"
                    className="bg-gradient-to-r from-blue-500 to-blue-400 text-white rounded-full px-3 py-1 text-sm font-medium shadow hover:scale-105 transition-transform focus:outline-none"
                    onClick={() => handleQuickAction(action.message)}
                    disabled={curveAiLoading}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
            {/* Contextual Suggestions */}
            {showSuggestions && (
              <div className="flex flex-wrap gap-2 px-3 pb-2">
                {suggestions.map((sugg, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-full px-3 py-1 text-sm font-medium shadow transition-all duration-150 hover:scale-105 hover:shadow-lg hover:brightness-110 focus:outline-none"
                    onClick={() => handleQuickAction(sugg)}
                    disabled={curveAiLoading}
                  >
                    {sugg}
                  </button>
                ))}
              </div>
            )}
          </Card>
        </section>
      </main>
      {/* About Modal */}
      {showAbout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-sm w-full relative animate-fade-in">
            <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-xl font-bold" onClick={() => setShowAbout(false)} aria-label="Close">&times;</button>
            <h2 className="text-lg font-semibold mb-2">About Curve Ai</h2>
            <p className="text-sm text-gray-700 mb-2">Curve Ai is your smart study assistant. You can ask it to quiz you, make a study plan, explain concepts, or help you focus your revision using your uploaded documents.</p>
            <p className="text-xs text-gray-400">Powered by AI. Your data is private and secure.</p>
          </div>
        </div>
      )}
      <style>{`
        .animate-fade-in {
          animation: fadeIn 0.4s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Dashboard; 